import { ServiceLayout } from "layouts";
import { Section, Container, ServiceMenu } from "components";
import Link from "next/link";
import Head from "next/head";
import * as cx from "classnames/bind";
import s from "styles/Service.module.scss";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";

export default function Privacy({ policies }) {
  const { t } = useTranslation();
  return (
    <>
      <Head>
        <title key="title">Cookies - Service - Cocktail Club</title>
        <meta
          key="ogTitle"
          property="og:title"
          content="Cookies - Service - Cocktail Club"
        />
        <meta
          key="ogUrl"
          property="og:url"
          content="https://cocktailclub.com/service/cookies"
        />
        <link
          key="canonical"
          rel="canonical"
          href="https://cocktailclub.com/service/cookies"
        />
      </Head>
      <ServiceLayout>
        <Section>
          <Container>
            <ServiceMenu title={t("Cookies")} />
            <div className={cx(s.PolicyContent)}>
              <p>
                <b>
                  Cocktail Club / Cocktail Club Productions SLU – Cookie policy
                </b>
              </p>
              <p>Last updated: 18.08.22</p>
              <p>
                <strong>CONTENTS</strong>
              </p>
              <ol className={cx(s.PolicyContentList)}>
                <li>
                  <strong>WHAT ARE COOKIES AND WHAT DO THEY DO?</strong>
                </li>
                <li>
                  <strong>HOW WE USE COOKIES</strong>
                </li>
                <li>
                  <strong>CONSENT TO THE USE OF COOKIES ON THIS WEBSITE</strong>
                </li>
                <li>
                  <strong>
                    BLOCKING OUR USE OF COOKIES OR WITHDRAWAL OF YOUR CONSENT
                    FOR USE OF COOKIES BY THIS WEBSITE
                  </strong>
                </li>
                <li>
                  <strong>WHAT HAPPENS IF I BLOCK COOKIES?</strong>
                </li>
                <li>
                  <strong>WHAT COOKIES DO WE USE?</strong>
                </li>
                <li>
                  <strong>INFORMATION ABOUT US</strong>
                </li>
              </ol>
              <p>&nbsp;</p>
              <p>
                This Cookie Policy is intended to inform you how we use a
                technology called &ldquo;cookies&rdquo; and similar technologies
                on this Website or Application and to assist you by providing
                information on how you can manage the cookies stored on your
                devices. Please take a minute to read and understand the Cookie
                Policy.
              </p>
              <p>&nbsp;</p>
              <p>
                <strong>1. WHAT ARE COOKIES AND WHAT DO THEY DO?</strong>
              </p>
              <p>
                A &ldquo;cookie&rdquo; is a small text file that&rsquo;s stored
                on your computer, smartphone, tablet, or other device when you
                visit a website or use an app.
              </p>
              <p>
                Some cookies are deleted when you close your browser. These are
                known as session cookies. Others remain on your device until
                they expire, or you delete them from your cache. These are known
                as persistent cookies and enable us to remember things about you
                as a returning visitor.
              </p>
              <p>
                Information gathered through cookies and web server logs may
                include the date and time of visits, the pages viewed, and the
                time spent at our Website.
              </p>
              <p>&nbsp;</p>
              <p>
                <strong>2. HOW WE USE COOKIES</strong>
              </p>
              <p>We use cookies and other similar technologies to:</p>
              <ul>
                <li>
                  Give you a better online experience and track website and
                  application performance
                </li>
                <li>
                  Provide products and services that you request and to provide
                  a secure online environment
                </li>
                <li>Manage our marketing relationships</li>
                <li>
                  Help us make our website and applications more relevant to you
                </li>
              </ul>
              <p>
                We do not generally store any personal information that you
                provide to us in a cookie.
              </p>
              <p>
                Most of our cookies expire within 90 days, although our
                analytics cookies may persist for between 2 and 10 years.
              </p>
              <p>
                If you delete cookies relating to this website we will not
                remember things about you and you will be treated as a
                first-time visitor the next time you visit the site.
              </p>
              <p>&nbsp;</p>
              <p>
                <strong>
                  3. CONSENT TO THE USE OF COOKIES ON THIS WEBSITE
                </strong>
              </p>
              <p>
                We will only set cookies on your device with your prior consent,
                if this is required by law. If you have consented to the use of
                cookies on our Website or Application, you have the right to
                withdraw your consent at any time.
              </p>
              <p>&nbsp;</p>
              <p>
                <strong>
                  4. BLOCKING OUR USE OF COOKIES OR WITHDRAWAL OF YOUR CONSENT
                  FOR USE OF COOKIES BY THIS WEBSITE
                </strong>
              </p>
              <p>
                You can block our use of cookies by activating the settings in
                your browser. Please visit&nbsp;
                <a href="http://www.allaboutcookies.org/">All About Cookies</a>
                &nbsp;where you can find comprehensive information on cookie
                management and blocking for a wide variety of browsers.
              </p>
              <p>&nbsp;</p>
              <p>
                <strong>5. WHAT HAPPENS IF I BLOCK COOKIES?</strong>
              </p>
              <p>
                In order to use certain services offered through our Website or
                Application, your device must accept cookies. If you choose to
                withhold consent, or subsequently block cookies, some aspects of
                the Website or Application may not work properly and you may not
                be able to access all or part of our Website or Application.
              </p>
              <p>&nbsp;</p>
              <p>
                <strong>6. WHAT COOKIES DO WE USE?</strong>
              </p>
              <p>
                Please review the table below for more detailed information on
                the specific types of cookies we use.
              </p>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Provider</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                    <th>Type</th>
                    <th>Segment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>_ga</td>
                    <td>google.com</td>
                    <td>
                      Registers a unique ID that is used to generate statistical
                      data on how the visitor uses the website.
                    </td>
                    <td>2 years</td>
                    <td>HTTP Cookie</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_gid</td>
                    <td>google.com</td>
                    <td>
                      Registers a unique ID that is used to generate statistical
                      data on how the visitor uses the website.
                    </td>
                    <td>1 day</td>
                    <td>HTTP Cookie</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_gat</td>
                    <td>google.com</td>
                    <td>Used by Google Analytics to throttle request rate</td>
                    <td>1 day</td>
                    <td>HTTP Cookie</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hjAbsoluteSessionInProgress</td>
                    <td>hotjar.com</td>
                    <td>
                      Used to detect the first pageview session of a user.
                    </td>
                    <td>30 minutes</td>
                    <td>Boolean</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hjSessionUser_</td>
                    <td>hotjar.com</td>
                    <td>
                      Set when a user first lands on a page. Persists the Hotjar
                      User ID which is unique to that site. Ensures data from
                      subsequent visits to the same site are attributed to the
                      same user ID.
                    </td>
                    <td>1 year</td>
                    <td>JSON data</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hjSession_</td>
                    <td>hotjar.com</td>
                    <td>
                      Holds current session data. Ensures subsequent requests in
                      the session window are attributed to the same session.
                    </td>
                    <td>30 minutes</td>
                    <td>JSON data</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hjTLDTest</td>
                    <td>hotjar.com</td>
                    <td>
                      We try to store the _hjTLDTest cookie for different URL
                      substring alternatives until it fails. Enables us to try
                      to determine the most generic cookie path to use, instead
                      of page hostname. It means that cookies can be shared
                      across subdomains (where applicable). After this check,
                      the cookie is removed.
                    </td>
                    <td>Session</td>
                    <td>Boolean</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hjIncludedInPageviewSample</td>
                    <td>hotjar.com</td>
                    <td>
                      Set to determine if a user is included in the data
                      sampling defined by your site’s pageview limit.
                    </td>
                    <td>30 minutes</td>
                    <td>Boolean</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hjIncludedInSessionSample</td>
                    <td>hotjar.com</td>
                    <td>
                      Set to determine if a user is included in the data
                      sampling defined by your site’s daily session limit.
                    </td>
                    <td>30 minutes</td>
                    <td>Boolean</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hp2_hld7831895001171715</td>
                    <td>heap.io</td>
                    <td>
                      Used to determine which domain a cookie can be set on
                      (since public suffix domains block setting cookies on the
                      top level)
                    </td>
                    <td>Should not persist</td>
                    <td>HTTP Cookie</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hp2_id.</td>
                    <td>heap.io</td>
                    <td>User cookie (stores user_id, identity, other ids)</td>
                    <td>13 months</td>
                    <td>HTTP Cookie</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>_hp2_ses_props.</td>
                    <td>heap.io</td>
                    <td>
                      Session properties cookie (stores timestamp and cookie
                      domain/path)
                    </td>
                    <td>30 minutes</td>
                    <td>HTTP Cookie</td>
                    <td>Analytical</td>
                  </tr>
                  <tr>
                    <td>CookieConsent</td>
                    <td>cocktailclub.com</td>
                    <td>
                      Stores the user’s cookie consent state for the current
                      domain
                    </td>
                    <td>1 year</td>
                    <td>HTTP Cookie</td>
                    <td>Necessary</td>
                  </tr>
                  <tr>
                    <td>aelia_cs_selected_currency</td>
                    <td>cocktailclub.com</td>
                    <td>
                      Stores the user’s selected currency for the current domain
                    </td>
                    <td>1 year</td>
                    <td>HTTP Cookie</td>
                    <td>Functionality</td>
                  </tr>
                  <tr>
                    <td>age-verification</td>
                    <td>cocktailclub.com</td>
                    <td>
                      Stores the user’s age consent state for the current domain
                    </td>
                    <td>1 year</td>
                    <td>HTTP Cookie</td>
                    <td>Necessary</td>
                  </tr>
                  <tr>
                    <td>cookies.js</td>
                    <td>cocktailclub.com</td>
                    <td>
                      Provide necessary cookies. Stores the user’s cookie
                      consent state for the current domain
                    </td>
                    <td>1 year</td>
                    <td>HTTP Cookie</td>
                    <td>Necessary</td>
                  </tr>
                  <tr>
                    <td>mailchimp_landing_site</td>
                    <td>mailchimp.com</td>
                    <td>This is the “first page” the customer lands on.</td>
                    <td>30 days</td>
                    <td>HTTP Cookie</td>
                    <td>Functionality</td>
                  </tr>
                  <tr>
                    <td>mailchimp_user_email</td>
                    <td>mailchimp.com</td>
                    <td>
                      This is the shopper’s email address. We use this to track
                      cart data and also allow for Mailchimp to send the shopper
                      back to your site to reload the cart from any device and
                      make sure the cart is populated correctly.
                    </td>
                    <td>30 days</td>
                    <td>HTTP Cookie</td>
                    <td>Functionality</td>
                  </tr>
                  <tr>
                    <td>wcacr_user_country</td>
                    <td>cocktailclub.com</td>
                    <td>Stores the user’s country for the current domain</td>
                    <td>2 days</td>
                    <td>HTTP Cookie</td>
                    <td>Functionality</td>
                  </tr>
                  <tr>
                    <td>wp-settings</td>
                    <td>cocktailclub.com</td>
                    <td>
                      The cookie is used to check whether your web browser is
                      set to allow, or reject cookies.
                    </td>
                    <td>1 year</td>
                    <td>HTTP Cookie</td>
                    <td>Necessary</td>
                  </tr>
                  <tr>
                    <td>wp-settings-time</td>
                    <td>cocktailclub.com</td>
                    <td>Time at which wp-settings-user was set</td>
                    <td>1 year</td>
                    <td>HTTP Cookie</td>
                    <td>Necessary</td>
                  </tr>
                </tbody>
              </table>
              <p>&nbsp;</p>
              <p>
                <strong>7. INFORMATION ABOUT US</strong>
              </p>
              <p>
                In the present Privacy Policy and in the connected&nbsp;
                <a href="https://cocktailclub.com/service/terms">
                  Terms and Conditions&nbsp;
                </a>
                of Website Use and&nbsp;Cookie Policy, &ldquo;We&rdquo; or
                &ldquo;us&rdquo; or &ldquo;CC&rdquo; or &ldquo;CC/QAS&rdquo;
                means Cocktail Club / Quenched AS with its registered office in
                Madserud All&eacute; 34, 0274 Oslo, Norway.
              </p>
              <p>&nbsp;</p>
              <br />
              <br />
            </div>
          </Container>
        </Section>
      </ServiceLayout>
    </>
  );
}

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale)),
  },
});
